import { Outlet } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import useSchools from '../hooks/useSchools';
import Loader from '../reusable/Loader';
import PageHeader from '../reusable/PageHeader';
import { NAVIGATION_LINKS_DATA, XQ_SUPPORT_EMAIL } from '../../constants';
import SelectDropdown from '../reusable/SelectDropdown';
import useUploadProgress from '../hooks/useUploadProgress';
import { ErrorBoundary } from 'react-error-boundary';
import DataQcEmptyStateImage from '../assets/data-qc-empty-state-image.svg';
import GenericErrorCard from '../reusable/GenericErrorCard';
import CustomButton from '../reusable/CustomButton';

import './DataQcLayout.css';

const pageDescription = 'Review uploaded district data and make sure it\'s accurate.';
const allSchoolsValue = 'All schools';

function DataQcLayout() {

  const { schools: { isPending: isSchoolsDataPending, error: schoolsError, data: schools }, dataQcSchool, updateDataQcSchool } = useSchools();

  const { uploadProgressSummary: { isPending: isUploadProgressPending, error: uploadProgressError, data: uploadProgress }} = useUploadProgress();

  if (isUploadProgressPending) return <Loader />;
  if (uploadProgressError) return <p className="center-text">Error occured while fetching upload progress data!</p>;

  const handleChange = (event: SelectChangeEvent) => {
    const { value } = event.target;

    if (value === allSchoolsValue) updateDataQcSchool('');
    else updateDataQcSchool(value);
  };

  const getDropdownItems = () => {
    if (!schools) return [];

    let items = [{ label: allSchoolsValue, value: allSchoolsValue}];
    schools.forEach((school) => items.push({ label: school.name, value: school.id }));

    return items;
  };

  return (
    <>
      <PageHeader title={NAVIGATION_LINKS_DATA.DATA_QC.label} pageDescription={pageDescription} />
      <ErrorBoundary fallback={
        <div className="main-content">
          <GenericErrorCard 
            title="Something went wrong while loading the data"
            text={<>Please try refreshing the page. If the issue continues, <a href={`mailto:${XQ_SUPPORT_EMAIL}`} className="link-color">contact support</a> for help.</>}
            imgSrc={DataQcEmptyStateImage}
            button={
              <CustomButton
                variant="primary"
                buttonText="Refresh page"
                buttonAction={() => window.location.reload()}
              />
            }/>
        </div>
      }>
        <div className="select-school-bar">
          {isSchoolsDataPending ? <Loader /> : null}
          {schoolsError ? <p>Missing schools data!</p> : null}
          { schools &&
              <>
                <span>Display data for</span>
                <SelectDropdown
                  label="School"
                  selectedValue={dataQcSchool ? dataQcSchool : 'All schools'}
                  handleChange={handleChange}
                  items={getDropdownItems()}
                  isClearable={false}
                />
              </>
          }
        </div>
        <Outlet 
          context={{
            uploadProgress,
            schools,
            dataQcSchool
          }}
        />
      </ErrorBoundary>
    </>
  );
}

export default DataQcLayout;